import { useEffect, useRef } from 'react';

/**
 * InputTimed - A debounced input component
 * Delays onCommit callback by specified milliseconds (default 500ms)
 * Passes all other props directly to the input element
 * Supports input and textarea via 'type' prop (type="textarea" for textarea)
 * 
 * NOTE: This component is uncontrolled - it reads the value from the DOM
 * when the debounce timer fires, avoiding unnecessary re-renders.
 */
export default function InputTimed({ delay = 500, onCommit, defaultValue, ...props }) {
  // Ref to store the timeout ID for debouncing
  const timeoutRef = useRef(null);
  // Ref to the input/textarea element to read value on commit
  const inputRef = useRef(null);

  // Handle input changes with debouncing
  const handleChange = () => {
    // Clear previous timeout if exists (debounce logic)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout to read value and call onCommit after delay
    timeoutRef.current = setTimeout(() => {
      // Read value directly from DOM when timer fires
      // Use optional chaining in case component unmounted before timer fires
      const value = inputRef.current?.value;
      if (value !== undefined) {
        onCommit?.(value);
      }
    }, delay);
  };

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Render textarea if type is 'textarea', otherwise render input
  if (props.type === 'textarea') {
    const { type, ...restProps } = props;
    return <textarea {...restProps} ref={inputRef} defaultValue={defaultValue} onChange={handleChange} />;
  }
  return <input {...props} ref={inputRef} defaultValue={defaultValue} onChange={handleChange} />;
}
